import { type TFunction } from "i18next";
import { ChevronRight } from "lucide-react";

import { RelativeDateCell } from "@/components/RelativeDateCell";
import { WFPButton } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDateTimeCompact, formatRelativeDateTime } from "@/utils/relative-time";

import { MetricValue } from "./MetricValue";
import { NameCell } from "./NameCell";
import { SortableColumnHeader } from "./SortableColumnHeader";
import { TextValue } from "./TextValue";
import { COMPACT_HEADER_CLASS, NUMBER_COLUMN_WIDTH_CLASS, NUMERIC_HEADER_CLASS } from "./constants";
import { type TraderColumnDef, type TraderListItem } from "./types";
import { getLastActiveTimestamp, getTraderArea, getTraderName } from "./utils";

const createNumberColumn = (
  id: string,
  label: string,
  accessor: (row: TraderListItem) => number | null | undefined
): TraderColumnDef => ({
  id,
  accessorFn: accessor,
  header: ({ column }) => <SortableColumnHeader align="center" column={column} label={label} />,
  cell: ({ row }) => <MetricValue value={accessor(row.original)} />,
  sortDescFirst: false,
  sortingFn: "basic",
  sortUndefined: "last",
  meta: {
    cellClassName: cn(NUMBER_COLUMN_WIDTH_CLASS, "px-3 text-center"),
    headerClassName: cn(NUMBER_COLUMN_WIDTH_CLASS, NUMERIC_HEADER_CLASS),
  },
});

const createColumns = ({
  onClickView,
  t,
}: {
  onClickView: (trader: TraderListItem) => void;
  t: TFunction;
}): TraderColumnDef[] => {
  return [
    {
      id: "view",
      header: () => <span className="sr-only">{t("common.view")}</span>,
      cell: ({ row }) => (
        <WFPButton
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => onClickView(row.original)}
        >
          {t("common.view")}
          <ChevronRight className="size-4" aria-hidden="true" />
        </WFPButton>
      ),
      enableSorting: false,
      meta: {
        cellClassName: "w-0 px-3",
        headerClassName: "w-0",
      },
    },
    {
      id: "name",
      accessorFn: getTraderName,
      header: ({ column }) => (
        <SortableColumnHeader column={column} label={t("traders.list.columns.name", "Name")} />
      ),
      cell: ({ row }) => <NameCell trader={row.original} />,
      sortDescFirst: false,
      sortingFn: "alphanumeric",
      sortUndefined: "last",
      meta: {
        cellClassName: "min-w-48 max-w-64 whitespace-normal px-3",
        headerClassName: cn("min-w-48 max-w-64", COMPACT_HEADER_CLASS),
      },
    },
    {
      id: "location_name",
      accessorFn: getTraderArea,
      header: ({ column }) => (
        <SortableColumnHeader column={column} label={t("traders.list.columns.area", "Area")} />
      ),
      cell: ({ row }) => (
        <TextValue
          className="block whitespace-normal leading-tight"
          value={getTraderArea(row.original)}
        />
      ),
      sortDescFirst: false,
      sortingFn: "alphanumeric",
      sortUndefined: "last",
      meta: {
        cellClassName: "min-w-32 max-w-56 whitespace-normal px-3",
        headerClassName: cn("min-w-32 max-w-56", COMPACT_HEADER_CLASS),
      },
    },
    {
      id: "last_active",
      accessorFn: getLastActiveTimestamp,
      header: ({ column }) => (
        <SortableColumnHeader
          column={column}
          label={t("traders.list.columns.lastActive", "Last active")}
        />
      ),
      cell: ({ row }) => (
        <RelativeDateCell
          relative={formatRelativeDateTime(row.original.last_active, t)}
          absolute={formatDateTimeCompact(row.original.last_active)}
        />
      ),
      sortDescFirst: false,
      sortingFn: "basic",
      sortUndefined: "last",
      meta: {
        cellClassName: "min-w-36 px-3",
        headerClassName: cn("min-w-36", COMPACT_HEADER_CLASS),
      },
    },
    createNumberColumn(
      "direct_offers_count",
      t("traders.list.columns.directOffers", "Direct offers"),
      row => row.direct_offers_count
    ),
    createNumberColumn(
      "market_offers_count",
      t("traders.list.columns.demandOffers", "Demand offers"),
      row => row.market_offers_count
    ),
    createNumberColumn(
      "accepted_count",
      t("traders.list.columns.offersAccepted", "Offers accepted"),
      row => row.accepted_count
    ),
    createNumberColumn(
      "exchange_done_count",
      t("traders.list.columns.exchanges", "Exchanges"),
      row => row.exchange_done_count
    ),
  ];
};

export { createColumns };
